import FormatQuote from '@mui/icons-material/FormatQuote';
import PlayArrow from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { type DeliveryGalleryItem } from '@/services/deliveries';
import { formatClipDuration } from '@/utils/deliveryFormat';

type Props = { item: DeliveryGalleryItem };

/** The dark pill both badges are drawn on, so they match whatever is behind them. */
const pill = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  px: 2,
  py: 0.75,
  borderRadius: 999,
  bgcolor: 'rgba(8, 8, 10, 0.68)',
  backdropFilter: 'blur(6px)',
  color: '#FFFFFF',
} as const;

/**
 * What a tile says about itself before it is opened.
 *
 * Two marks, and both answer a question a visitor has *before* tapping. A
 * video tile that looks exactly like a photograph tile is a tile that opens a
 * full-screen feed when somebody expected a still picture, and the duration
 * next to the play mark is the difference between "worth a tap" and "how long
 * is this".
 *
 * The quotation mark says the handover behind this tile carries a customer's
 * own words, which is the strongest thing in the archive and otherwise
 * invisible until the viewer is open.
 *
 * Both are `aria-hidden`: the tile's accessible name already states what kind
 * of medium it is and whether there is a quotation, and a screen reader
 * hearing "play, 0:06, quote" before the name is noise.
 */
export function DeliveryTileBadges({ item }: Props) {
  const isVideo = item.media.kind === 'video';
  const duration = item.media.durationSec;

  return (
    <Stack
      aria-hidden
      direction="row"
      spacing={2}
      sx={{ position: 'absolute', top: 10, left: 10, right: 10, pointerEvents: 'none' }}
    >
      {isVideo && (
        <Box sx={pill}>
          <PlayArrow sx={{ fontSize: 16 }} />
          {duration !== null && (
            <Typography variant="caption" className="tabular-nums" sx={{ fontWeight: 700 }}>
              {formatClipDuration(duration)}
            </Typography>
          )}
        </Box>
      )}

      {item.delivery.testimonial && (
        <Box sx={{ ...pill, ml: 'auto' }}>
          <FormatQuote sx={{ fontSize: 16 }} />
        </Box>
      )}
    </Stack>
  );
}
